// Load Board V1 — Web (THT-695). Turn a browser geolocation fix into a
// dispatcher-facing pickup string ("Dallas, TX").
//
// This goes through the Maps JavaScript SDK's Geocoder, NOT the REST Geocoding
// web service, because tach-ui's Google key (REACT_APP_GOOGLE_MAPS_API_KEY) is
// HTTP-referrer restricted — and Google rejects referrer-restricted keys on the
// REST endpoint outright ("API keys with referer restrictions cannot be used
// with this API", status REQUEST_DENIED). That call therefore ALWAYS came back
// empty, so every "Use current location" silently degraded to the raw
// "32.7767, -96.7970" fallback. Referrer restrictions are exactly what the JS
// SDK is designed for, so the same key works there.
//
// The SDK itself is already loaded by CityAutocomplete (useJsApiLoader, loader
// id 'google-map-script') — the sibling field in this very modal — so there is
// no second script to pull in here. If it is unavailable, or the lookup fails,
// this returns null and the caller falls back to a lat/lng display, mirroring
// the mobile flow's "reverse geocoding unavailable" branch.

export interface ReverseGeocodeResult {
  display: string;
  city?: string;
  state?: string;
}

interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GeocoderResult {
  address_components?: GoogleAddressComponent[];
}

// The button can be clicked while the SDK script is still in flight (it starts
// loading when the modal mounts), so wait a bounded moment for it rather than
// failing instantly on a race the user would read as "geocoding is broken".
// Short, because the geolocation fix that precedes this may itself have taken
// seconds — a long extra wait reads as a hung button.
const SDK_WAIT_MS = 3000;
const SDK_POLL_MS = 100;

const waitForMapsSdk = async (timeoutMs = SDK_WAIT_MS): Promise<any | null> => {
  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const maps = (window as any).google?.maps;
    if (maps?.Geocoder) return maps;
    if (Date.now() >= deadline) return null;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, SDK_POLL_MS));
  }
};

const pickComponent = (
  components: GoogleAddressComponent[],
  type: string,
  short = false
): string | undefined => {
  const match = components.find((c) => c.types?.includes(type));
  if (!match) return undefined;
  return short ? match.short_name : match.long_name;
};

// Google returns a LIST of results for one fix, ordered most-specific first:
// a street address, then the neighborhood, the city, the county, the state.
// Reading only `results[0]` therefore reads whatever happens to be nearest the
// point — and the most specific result often carries a `sublocality`
// (neighborhood) but no `locality`, which is how a city field ends up showing a
// neighborhood. Search every result for the component we actually want; the
// city-level entry is in there, just not always in the first one.
const pickAcross = (
  results: GeocoderResult[],
  type: string,
  short = false
): string | undefined => {
  for (const result of results) {
    const value = pickComponent(result.address_components ?? [], type, short);
    if (value) return value;
  }
  return undefined;
};

// Ordered best-city-first. `locality` is the city almost everywhere; the rest
// are the regional exceptions, ending with the neighborhood as a last resort
// (still better than raw coordinates for a dispatcher).
const CITY_COMPONENT_PRIORITY = [
  'locality',
  // UK-style addressing: Google files the town here rather than as a locality.
  'postal_town',
  // In Brazil (and much of Latin America) the município — the city — is
  // level_2, and many addresses carry NO locality at all. In the US level_2 is
  // the county, which only gets consulted when no locality exists anywhere in
  // the response, where a county still beats a neighborhood.
  'administrative_area_level_2',
  // US townships and other small civil divisions, for fixes outside city limits.
  'administrative_area_level_3',
  // Neighborhood. Last, so it can never outrank a real city name.
  'sublocality',
];

// `geocode` is promise-based on current SDKs but callback-only on older ones,
// so go through the callback form — it is supported everywhere.
const geocodeLatLng = (
  maps: any,
  lat: number,
  lng: number
): Promise<GeocoderResult[]> =>
  new Promise((resolve, reject) => {
    new maps.Geocoder().geocode(
      { location: { lat, lng } },
      (results: GeocoderResult[] | null, status: string) => {
        if (status !== 'OK' || !results?.length) {
          reject(new Error(`Geocoder status: ${status}`));
          return;
        }
        resolve(results);
      }
    );
  });

export const reverseGeocode = async (
  lat: number,
  lng: number
): Promise<ReverseGeocodeResult | null> => {
  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) return null;

  const maps = await waitForMapsSdk();
  if (!maps) return null;

  try {
    const results = await geocodeLatLng(maps, lat, lng);

    // Each tier is searched across ALL results before the next tier is tried,
    // so a city named only in a broader result still wins over a neighborhood
    // sitting in the most specific one.
    let city: string | undefined;
    for (const type of CITY_COMPONENT_PRIORITY) {
      city = pickAcross(results, type);
      if (city) break;
    }

    const state = pickAcross(results, 'administrative_area_level_1', true);
    const display = [city, state].filter(Boolean).join(', ');

    if (!display) return null;
    return { display, city, state };
  } catch {
    return null;
  }
};
